import { buildVisibleModel } from '../components/pagination/pagination-model.js';

const createPageItem = (page, currentPage) => {
  const listItem = document.createElement('li');
  listItem.className = 'pagination__list-item';

  const anchor = document.createElement('a');
  anchor.className = 'pagination__item';
  anchor.href = '#';
  anchor.dataset.page = String(page);
  anchor.setAttribute('aria-label', `Go to page ${page}`);
  anchor.textContent = String(page);

  if (page === currentPage) {
    anchor.setAttribute('aria-current', 'page');
  }

  listItem.append(anchor);
  return listItem;
};

const createButtonItem = (type, disabled) => {
  const listItem = document.createElement('li');
  listItem.className = 'pagination__list-item';

  const button = document.createElement('button');
  button.className = `pagination__item pagination__item--icon pagination__item--${type}`;
  button.type = 'button';
  button.dataset.direction = type;
  button.setAttribute('aria-label', `Go to ${type === 'previous' ? 'previous' : 'next'} page`);
  button.disabled = disabled;

  const icon = document.createElement('span');
  icon.className = `pagination__icon ${
    type === 'previous'
      ? disabled
        ? 'pagination__icon--previous-disabled'
        : 'pagination__icon--previous'
      : disabled
      ? 'pagination__icon--next-disabled'
      : 'pagination__icon--next'
  }`;
  icon.setAttribute('aria-hidden', 'true');

  button.append(icon);
  listItem.append(button);
  return listItem;
};

const createMoreItem = (hiddenPages, menuId) => {
  const listItem = document.createElement('li');
  listItem.className = 'pagination__list-item pagination__list-item--more';

  const details = document.createElement('details');
  details.className = 'pagination__more';

  const summary = document.createElement('summary');
  summary.className = 'pagination__item pagination__item--more';
  summary.setAttribute(
    'aria-label',
    `Show hidden pages ${hiddenPages[0]} through ${hiddenPages[hiddenPages.length - 1]}`
  );
  summary.setAttribute('aria-controls', menuId);
  summary.setAttribute('aria-expanded', 'false');

  const icon = document.createElement('img');
  icon.className = 'pagination__more-icon';
  icon.src = '/images/svgs/ellipsis.svg';
  icon.alt = '';
  summary.append(icon);

  const menu = document.createElement('div');
  menu.className = 'pagination__more-menu';
  menu.id = menuId;
  menu.setAttribute('aria-label', `Hidden pages ${hiddenPages[0]} through ${hiddenPages[hiddenPages.length - 1]}`);

  hiddenPages.forEach((page) => {
    const option = document.createElement('a');
    option.className = 'pagination__more-option';
    option.href = '#';
    option.dataset.page = String(page);
    option.setAttribute('aria-label', `Go to page ${page}`);

    const check = document.createElement('span');
    check.className = 'pagination__more-check pagination__more-check--spacer';
    check.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'pagination__more-label';
    label.textContent = String(page);

    option.append(check, label);
    menu.append(option);
  });

  details.append(summary, menu);
  details.addEventListener('toggle', () => {
    summary.setAttribute('aria-expanded', String(details.open));
  });
  details.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      details.open = false;
      summary.focus();
    }
  });

  listItem.append(details);
  return listItem;
};

const initializePagination = (container, index) => {
  const minPage = 1;
  const maxPage = Math.max(Number.parseInt(container.dataset.totalPages ?? '1', 10), 1);
  const initialPage = Number.parseInt(container.dataset.initialPage ?? '1', 10);
  const navLabel = container.dataset.navLabel ?? 'Pagination';
  const mobileQuery = window.matchMedia('(width < 768px)');
  let currentPage = Math.min(Math.max(initialPage, minPage), maxPage);

  const nav = document.createElement('nav');
  nav.setAttribute('aria-label', navLabel);

  const pagination = document.createElement('ul');
  pagination.className = `pagination${maxPage <= 7 ? ' pagination--under-seven' : ''}`;

  nav.append(pagination);
  container.replaceChildren(nav);

  const focusCurrentItem = () => {
    window.requestAnimationFrame(() => {
      const currentItem = pagination.querySelector('.pagination__item[aria-current="page"]');
      if (currentItem instanceof HTMLElement) {
        currentItem.focus();
      }
    });
  };

  const clearPressedState = () => {
    pagination.querySelectorAll('.pagination__item.is-pressed, .pagination__more-option.is-pressed').forEach((item) => {
      item.classList.remove('is-pressed');
    });
  };

  const render = () => {
    pagination.innerHTML = '';
    pagination.append(createButtonItem('previous', currentPage === minPage));
    const maxVisibleItems = mobileQuery.matches ? 5 : 7;

    const { leadingHidden, visiblePages, trailingHidden } = buildVisibleModel(
      minPage,
      maxPage,
      currentPage,
      maxVisibleItems
    );

    visiblePages.forEach((page, visibleIndex) => {
      pagination.append(createPageItem(page, currentPage));

      if (visibleIndex === 0 && leadingHidden.length) {
        pagination.append(createMoreItem(leadingHidden, `pagination-more-${index}-leading`));
      }

      if (visibleIndex === visiblePages.length - 2 && trailingHidden.length) {
        pagination.append(createMoreItem(trailingHidden, `pagination-more-${index}-trailing`));
      }
    });

    pagination.append(createButtonItem('next', currentPage === maxPage));
  };

  pagination.addEventListener('pointerdown', (event) => {
    const pressedItem = event.target.closest('.pagination__item, .pagination__more-option');

    if (pressedItem && pagination.contains(pressedItem)) {
      clearPressedState();
      pressedItem.classList.add('is-pressed');
    }
  });

  pagination.addEventListener('click', (event) => {
    const pageTarget = event.target.closest('[data-page]');
    const directionButton = event.target.closest('button[data-direction]');

    if (pageTarget && pagination.contains(pageTarget)) {
      event.preventDefault();
      const nextPage = Number.parseInt(pageTarget.dataset.page ?? '', 10);

      if (!Number.isNaN(nextPage)) {
        currentPage = nextPage;
        render();
        clearPressedState();
        if (event.detail === 0) {
          focusCurrentItem();
        } else if (pageTarget instanceof HTMLElement) {
          pageTarget.blur();
        }
      }
      return;
    }

    if (directionButton && pagination.contains(directionButton)) {
      const direction = directionButton.dataset.direction;

      if (direction === 'previous' && currentPage > minPage) {
        currentPage -= 1;
      }

      if (direction === 'next' && currentPage < maxPage) {
        currentPage += 1;
      }

      render();
      clearPressedState();
      if (event.detail === 0) {
        focusCurrentItem();
      } else if (directionButton instanceof HTMLElement) {
        directionButton.blur();
      }
    }
  });

  pagination.addEventListener('pointerup', clearPressedState);
  pagination.addEventListener('pointercancel', clearPressedState);
  pagination.addEventListener('mouseleave', clearPressedState);

  mobileQuery.addEventListener('change', render);

  render();
};

export function initPagination(root = document) {
  root.querySelectorAll('[data-pagination]').forEach((container, index) => {
    if (!(container instanceof HTMLElement) || container.dataset.paginationBound === 'true') {
      return;
    }

    container.dataset.paginationBound = 'true';
    initializePagination(container, index);
  });
}

initPagination();
